import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';

import { useAnalyticsTimeouts } from '../../hooks/use-analytics-timeouts';

interface Props {
  experimentId: string;
}

/**
 * Page-level notice when one or more experiment panels timed out. Timeouts are
 * not auto-retried (they mean the analytics service is overloaded), so this
 * gives the user a single manual Retry for every timed-out panel instead.
 */
export function AnalyticsTimeoutBanner({ experimentId }: Props) {
  const { t } = useTranslation('experiments');
  const { hasTimeouts, isRetrying, retry } = useAnalyticsTimeouts(experimentId);
  if (!hasTimeouts) return null;
  return (
    <Alert className="border-amber-300 bg-amber-50 text-amber-900 [&>svg]:text-amber-700">
      <Clock className="h-4 w-4" aria-hidden />
      <AlertTitle>{t('detail.analyticsTimeout.title')}</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3 text-amber-900/90">
        <span>{t('detail.analyticsTimeout.description')}</span>
        <Button
          variant="outline"
          size="sm"
          className="cursor-pointer disabled:cursor-not-allowed"
          isLoading={isRetrying}
          onClick={retry}
        >
          {t('detail.analyticsTimeout.retry')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
